import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguageName } from '../use-language-name';
import type { CaptionLink } from './app-params';
import { DropTarget } from './IntentBands';

export interface CaptionLinkPanelProps {
  readonly link: CaptionLink;
  readonly onFiles: (files: File[]) => void;
  /** Back to the recipes: the visitor wants more than captions. */
  readonly onEdit: () => void;
  readonly dragging: boolean;
  readonly loading: string | false;
}

/**
 * Captions alone (`/?lang=tr`, `/?task=translate`, the captions pages'
 * button): no recipe is applied, so nothing is reframed or cut; the video
 * opens on the Captions tab, set up and ready to write.
 */
export function CaptionLinkPanel({ link, onFiles, onEdit, dragging, loading }: CaptionLinkPanelProps) {
  const { t } = useTranslation();
  const name = useLanguageName();
  const language = link.language && name(link.language);
  const title = link.task ? (language ? t('home.captions.titleTranslateFrom', { language }) : t('home.captions.titleTranslate')) : t('home.captions.title', { language });
  return (
    <section aria-labelledby="captions-title" className="border-t border-line-strong pt-4">
      <div className="-mx-3 grid gap-x-8 gap-y-5 rounded-[22px] bg-panel p-4 shadow-float ring-1 ring-line sm:-mx-5 sm:p-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-center">
        <div className="min-w-0">
          <h2 id="captions-title" className="text-[21px] leading-tight font-semibold tracking-[-0.03em] text-balance text-fg sm:text-[24px]">
            {title}
          </h2>
          <p className="mt-1.5 max-w-120 text-[15px] leading-snug text-pretty text-fg-muted">{t('home.captions.body')}</p>
        </div>
        <DropTarget dragging={dragging} loading={loading} onFiles={onFiles} hint={t('home.captions.hint')} />
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="group mt-4 inline-flex items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-fg-muted transition-colors duration-200 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-accent"
      >
        {t('home.captions.edit')}
        <ArrowRight size={14} className="transition-transform duration-200 ease-out-soft group-hover:translate-x-0.5" />
      </button>
    </section>
  );
}
